<script setup lang="ts">
import { computed, ref } from "vue";

interface Child {
  isListed: boolean;
  template: string;
  trekLocation: string;
  id?: string | number;
  uri?: string;
  title?: string;
  trekDistance?: string;
  trekElevation?: string;
  trekGrade?: string;
  coverImage?: { url?: string; alt?: string } | null;
  trekNavCoverImage?: { url?: string; alt?: string } | null;
}

const route = useRoute();
const site = useSite();
const isWorldwideTreksPage = useIsWorldwideTreksPage();
const { menuTreks, ensureMenuTreks } = useMenuTreks();
const isClassicHovering = ref(false);
const isWorldwideHovering = ref(false);
const isClassicDropdownVisible = ref(false);
const isWorldwideDropdownVisible = ref(false);
const delayTime = 150;

const listedChildren = computed(
  () =>
    site.value?.children?.filter(
      (child: Child) => child.isListed && child.template !== "treks",
    ) ?? [],
);

function treksForLocation(location: "Europe" | "Worldwide"): Child[] {
  const cards = (menuTreks.value ?? []).filter(
    (trek) =>
      trek?.isListed &&
      trek?.template === "treks" &&
      trek?.trekLocation === location,
  ) as Child[];
  if (cards.length) return cards;

  return (
    site.value?.children?.filter(
      (child: Child) =>
        child.isListed &&
        child.template === "treks" &&
        child.trekLocation === location,
    ) ?? []
  );
}

const classicTreks = computed(() => treksForLocation("Europe"));
const worldwideTreks = computed(() => treksForLocation("Worldwide"));

let showClassicTimeout: ReturnType<typeof setTimeout> | undefined;
const showClassicDropdown = () => {
  clearTimeout(showClassicTimeout);
  showClassicTimeout = setTimeout(() => {
    isClassicDropdownVisible.value = true;
  }, delayTime);
};

const hideClassicDropdown = () => {
  clearTimeout(showClassicTimeout);
  setTimeout(() => {
    if (!isClassicHovering.value) {
      isClassicDropdownVisible.value = false;
    }
  }, 150);
};

const classicMouseOverHandler = () => {
  isClassicHovering.value = true;
  void ensureMenuTreks();
  showClassicDropdown();
};

const classicMouseLeaveHandler = () => {
  isClassicHovering.value = false;
  hideClassicDropdown();
};

const classicLinkClickHandler = () => {
  isClassicDropdownVisible.value = false;
  isClassicHovering.value = false;
  clearTimeout(showClassicTimeout);
};

let showWorldwideTimeout: ReturnType<typeof setTimeout> | undefined;
const showWorldwideDropdown = () => {
  clearTimeout(showWorldwideTimeout);
  showWorldwideTimeout = setTimeout(() => {
    isWorldwideDropdownVisible.value = true;
  }, delayTime);
};

const hideWorldwideDropdown = () => {
  clearTimeout(showWorldwideTimeout);
  setTimeout(() => {
    if (!isWorldwideHovering.value) {
      isWorldwideDropdownVisible.value = false;
    }
  }, 150);
};

const worldwideMouseOverHandler = () => {
  isWorldwideHovering.value = true;
  void ensureMenuTreks();
  showWorldwideDropdown();
};

const worldwideMouseLeaveHandler = () => {
  isWorldwideHovering.value = false;
  hideWorldwideDropdown();
};

const worldwideLinkClickHandler = () => {
  isWorldwideDropdownVisible.value = false;
  isWorldwideHovering.value = false;
  clearTimeout(showWorldwideTimeout);
};

watch(isClassicDropdownVisible, (newValue) => {
  if (typeof document !== "undefined") {
    if (newValue) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }
});

watch(isWorldwideDropdownVisible, (newValue) => {
  if (typeof document !== "undefined") {
    if (newValue) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }
});
</script>

<template>
  <nav id="menu" class="main-menu">
    <ul>
      <li class="menu-item">
        <NuxtLink
          class="dropdown-toggle"
          to="/classic-treks"
          @mouseenter="classicMouseOverHandler"
          @mouseleave="classicMouseLeaveHandler"
        >
          Classic Treks
        </NuxtLink>

        <Transition name="fade-slide" mode="out-in">
          <div
            v-if="isClassicDropdownVisible"
            class="dropdown-menu treks-nav-wrap"
            @mouseover="classicMouseOverHandler"
            @mouseleave="classicMouseLeaveHandler"
          >
            <div v-for="trek in classicTreks" :key="trek.id" class="trek">
              <NuxtLink
                class="btn from-left"
                :to="{ path: `/${trek.uri}` }"
                @click="classicLinkClickHandler"
              >
                <div v-if="trek?.trekDistance" class="trek-stats">
                  <p v-if="trek?.trekDistance" class="trek-distance">
                    {{ trek?.trekDistance }}
                  </p>
                  <p v-if="trek?.trekElevation" class="trek-elevation">
                    {{ trek?.trekElevation }}
                  </p>
                </div>
                <div class="trek-image">
                  <NuxtPicture
                    v-if="trek?.trekNavCoverImage?.url"
                    loading="lazy"
                    fit="inside"
                    :src="trek.trekNavCoverImage.url"
                    :width="400"
                    :height="250"
                    :alt="trek.trekNavCoverImage.alt"
                    sizes="sm:200px md:300px lg:400px"
                  />
                </div>
                <div class="trek-title">
                  <h3>{{ trek?.title }}</h3>
                  <div v-if="trek?.trekGrade === 'Grade 1'" class="trek-grade">
                    <AppSVGGradeEasy />
                    <p>Easy</p>
                  </div>
                  <div v-if="trek?.trekGrade === 'Grade 2'" class="trek-grade">
                    <AppSVGGradeModerate />
                    <p>Moderate</p>
                  </div>
                  <div v-if="trek?.trekGrade === 'Grade 3'" class="trek-grade">
                    <AppSVGGradeDifficult />
                    <p>Difficult</p>
                  </div>
                  <div v-if="trek?.trekGrade === 'Grade 4'" class="trek-grade">
                    <AppSVGGradeToughest />
                    <p>Toughest</p>
                  </div>
                </div>
              </NuxtLink>
            </div>
            <div class="trek where-next">
              <NuxtLink
                class="btn from-left"
                to="/worldwide-treks/where-next"
                @click="classicLinkClickHandler"
              >
                <div class="trek-image">
                  <NuxtPicture
                    loading="lazy"
                    src="images/where-next.jpg"
                    width="400"
                    height="250"
                    alt="Where next image"
                    sizes="sm:200px md:300px lg:400px"
                  />
                </div>
                <div class="trek-title">
                  <h3>Where next?</h3>
                  <div class="trek-grade">
                    <AppSVGRegionsIcon />
                  </div>
                </div>
              </NuxtLink>
            </div>
            <div
              class="trek worldwide-treks-text"
              v-html="site?.comingSoonTrek"
            />
          </div>
        </Transition>
      </li>
      <li class="menu-item">
        <NuxtLink
          class="dropdown-toggle"
          to="/worldwide-treks"
          @mouseenter="worldwideMouseOverHandler"
          @mouseleave="worldwideMouseLeaveHandler"
        >
          Worldwide Treks
        </NuxtLink>

        <Transition name="fade-slide" mode="out-in">
          <div
            v-if="isWorldwideDropdownVisible"
            class="dropdown-menu treks-nav-wrap wwt"
            @mouseover="worldwideMouseOverHandler"
            @mouseleave="worldwideMouseLeaveHandler"
          >
            <div v-for="trek in worldwideTreks" :key="trek.id" class="trek">
              <NuxtLink
                class="btn from-left"
                :to="{ path: `/${trek.uri}` }"
                @click="worldwideLinkClickHandler"
              >
                <div v-if="trek?.trekDistance" class="trek-stats">
                  <p v-if="trek?.trekDistance" class="trek-distance">
                    {{ trek?.trekDistance }}
                  </p>
                  <p v-if="trek?.trekElevation" class="trek-elevation">
                    {{ trek?.trekElevation }}
                  </p>
                </div>
                <div class="trek-image">
                  <NuxtPicture
                    v-if="trek?.trekNavCoverImage?.url"
                    loading="lazy"
                    fit="inside"
                    :src="trek.trekNavCoverImage.url"
                    :width="400"
                    :height="250"
                    :alt="trek.trekNavCoverImage.alt"
                    sizes="sm:200px md:300px lg:400px"
                  />
                </div>
                <div class="trek-title">
                  <h3>{{ trek?.title }}</h3>
                  <div v-if="trek?.trekGrade === 'Grade 1'" class="trek-grade">
                    <AppSVGGradeEasy />
                    <p>Easy</p>
                  </div>
                  <div v-if="trek?.trekGrade === 'Grade 2'" class="trek-grade">
                    <AppSVGGradeModerate />
                    <p>Moderate</p>
                  </div>
                  <div v-if="trek?.trekGrade === 'Grade 3'" class="trek-grade">
                    <AppSVGGradeDifficult />
                    <p>Difficult</p>
                  </div>
                  <div v-if="trek?.trekGrade === 'Grade 4'" class="trek-grade">
                    <AppSVGGradeToughest />
                    <p>Toughest</p>
                  </div>
                </div>
              </NuxtLink>
            </div>
            <div
              class="trek worldwide-treks-text"
              v-html="site?.comingSoonTrek"
            />
          </div>
        </Transition>
      </li>
      <li
        v-for="item in listedChildren"
        :key="item.id"
        :class="`item-${item.id}`"
        class="menu-item"
      >
        <NuxtLink
          :to="{ path: `/${item.uri}` }"
          :aria-current="
            route.path.startsWith(`/${item.id}`) ? 'page' : undefined
          "
        >
          {{ item.menuText }}
        </NuxtLink>
      </li>
    </ul>
    <div id="feefoAwardTop">
      <NuxtPicture
        v-if="isWorldwideTreksPage"
        format="webp"
        fit="inside"
        :src="site?.feefoMobileBlue?.url"
        :width="site?.feefoMobileBlue?.width"
        :height="site?.feefoMobileBlue?.height"
        :alt="site?.feefoMobileBlue?.alt"
        sizes="sm:150px md:180px lg:250px"
      />
      <NuxtPicture
        v-else
        format="webp"
        fit="inside"
        :src="site?.feefoMobile?.url"
        :width="site?.feefoMobile?.width"
        :height="site?.feefoMobile?.height"
        :alt="site?.feefoMobile?.alt"
        sizes="sm:150px md:180px lg:250px"
      />
    </div>
    <Transition name="fade-slide" mode="out-in">
      <div v-if="isClassicDropdownVisible" class="nav-backdrop"></div>
    </Transition>
    <Transition name="fade-slide" mode="out-in">
      <div v-if="isWorldwideDropdownVisible" class="nav-backdrop wwt"></div>
    </Transition>
  </nav>
</template>
